if (!customElements.get('quick-add-modal')) {
  customElements.define(
    'quick-add-modal',
    class QuickAddModal extends ModalDialog {
      constructor() {
        super();
        this.modalContent = this.querySelector('[id^="QuickAddInfo-"]');

        this.addEventListener('product-info:loaded', ({ target }) => {
          target.addPreProcessCallback(this.preprocessHTML.bind(this));
        });
      }

      hide(preventFocus = false) {
        const cartNotification = document.querySelector('cart-notification') || document.querySelector('cart-drawer');
        if (cartNotification) cartNotification.setActiveElement(this.openedBy);
        this.modalContent.innerHTML = '';

        // Clean up any fitting room elements that might have been left behind
        if (window.removeFittingRoomElements) {
          setTimeout(() => {
            window.removeFittingRoomElements();
          }, 100);
          setTimeout(() => {
            window.removeFittingRoomElements();
          }, 1000);
        }

        if (preventFocus) this.openedBy = null;
        super.hide();
      }

      show(opener) {
        opener.setAttribute('aria-disabled', true);
        opener.classList.add('loading');
        opener.querySelector('.loading__spinner').classList.remove('hidden');

        fetch(opener.getAttribute('data-product-url'))
          .then((response) => response.text())
          .then((responseText) => {
            const responseHTML = new DOMParser().parseFromString(responseText, 'text/html');
            const productElement = responseHTML.querySelector('product-info');

            // Extract shipping transparency data before preprocessing
            this.extractShippingTransparencyData(responseHTML, productElement);
            
            this.preprocessHTML(productElement);
            HTMLUpdateUtility.setInnerHTML(this.modalContent, productElement.outerHTML);

            // Debug: Log what was inserted
            console.log('Quick View: Modal content inserted, checking structure...');
            console.log('Quick View: media-gallery exists:', !!this.modalContent.querySelector('media-gallery'));
            console.log('Quick View: product__media-item count:', this.modalContent.querySelectorAll('.product__media-item').length);
            console.log('Quick View: img count:', this.modalContent.querySelectorAll('img').length);

            // Debug: Log first few items and their classes
            const items = this.modalContent.querySelectorAll('.product__media-item');
            items.forEach((item, i) => {
              console.log(`Quick View: Item ${i} classes:`, item.className, 'display:', getComputedStyle(item).display);
            });

            // Immediately hide all images except first to prevent flash of all images
            this.immediatelyHideExtraImages();

            // Additional fallback: ensure first image is actually visible
            setTimeout(() => {
              this.ensureFirstImageVisible();
            }, 100);

            // Another check after modal animation completes
            setTimeout(() => {
              this.ensureFirstImageVisible();
            }, 300);

            // Load all shipping transparency dependencies before initializing
            this.loadShippingTransparencyDependencies()
              .then(() => {
                // Initialize shipping transparency after all dependencies are loaded
                this.initializeShippingTransparency();
              })
              .catch((error) => {
                console.error('Failed to load shipping transparency dependencies:', error);
                // Still try to initialize in case some dependencies loaded
                this.initializeShippingTransparency();
              });

            // Clean up any fitting room elements that might have been loaded with the product
            if (window.removeFittingRoomElements) {
              setTimeout(() => {
                window.removeFittingRoomElements();
              }, 500);
            }

            if (window.Shopify && Shopify.PaymentButton) {
              Shopify.PaymentButton.init();
            }
            if (window.ProductModel) window.ProductModel.loadShopifyXR();

            super.show(opener);
          })
          .finally(() => {
            opener.removeAttribute('aria-disabled');
            opener.classList.remove('loading');
            opener.querySelector('.loading__spinner').classList.add('hidden');
          });
      }

      async loadShippingTransparencyDependencies() {
        // Only load dependencies if we have shipping transparency data
        if (!this.originalSectionId || !this.newSectionId) {
          return;
        }

        console.log('🔄 Loading shipping transparency dependencies for quick view...');

        try {
          // Load CSS, JS, and execute API proxy script in parallel
          await Promise.all([
            this.loadShippingTransparencyCSS(),
            this.loadShippingTransparencyJS(),
            this.executeApiProxyScript()
          ]);

          console.log('✅ All shipping transparency dependencies loaded successfully');
        } catch (error) {
          console.error('❌ Error loading shipping transparency dependencies:', error);
          throw error;
        }
      }

      preprocessHTML(productElement) {
        productElement.classList.forEach((classApplied) => {
          if (classApplied.startsWith('color-') || classApplied === 'gradient')
            this.modalContent.classList.add(classApplied);
        });
        this.preventDuplicatedIDs(productElement);
        this.removeDOMElements(productElement);
        this.removeGalleryListSemantic(productElement);
        this.updateImageSizes(productElement);
        this.preventVariantURLSwitching(productElement);
      }

      preventVariantURLSwitching(productElement) {
        productElement.setAttribute('data-update-url', 'false');
      }

      removeDOMElements(productElement) {
        const pickupAvailability = productElement.querySelector('pickup-availability');
        if (pickupAvailability) pickupAvailability.remove();

        const productModal = productElement.querySelector('product-modal');
        if (productModal) productModal.remove();

        const modalDialog = productElement.querySelectorAll('modal-dialog');
        if (modalDialog) modalDialog.forEach((modal) => modal.remove());

        // Remove Complete the Look sections
        const completeTheLookSelectors = [
          '.complete-look-block',
          '[data-complete-look-block]',
          '[class*="complete-look"]',
          '[class*="complete_look"]',
          '[id*="complete-look"]',
          '[id*="complete_look"]'
        ];

        completeTheLookSelectors.forEach(selector => {
          try {
            const elements = productElement.querySelectorAll(selector);
            elements.forEach(element => {
              console.log('Removing Complete the Look element from quick view:', element);
              element.remove();
            });
          } catch (e) {
            // Ignore selector errors
          }
        });

        // Also remove any elements that contain "Complete the Look" text
        const allElements = productElement.querySelectorAll('*');
        allElements.forEach(element => {
          if (element.textContent && element.textContent.includes('Complete the Look')) {
            // Check if this is a direct container (not just containing the text within children)
            const directText = Array.from(element.childNodes)
              .filter(node => node.nodeType === Node.TEXT_NODE)
              .map(node => node.textContent.trim())
              .join(' ');
            
            if (directText.includes('Complete the Look') || element.querySelector('h1, h2, h3, h4, h5, h6')?.textContent?.includes('Complete the Look')) {
              console.log('Removing element containing "Complete the Look" text:', element);
              element.remove();
            }
          }
        });

        // Remove any product blocks that might contain Complete the Look
        const productBlocks = productElement.querySelectorAll('[class*="product__block"], [data-block-type], .shopify-section');
        productBlocks.forEach(block => {
          if (block.innerHTML && (
            block.innerHTML.includes('complete-look') || 
            block.innerHTML.includes('complete_look') ||
            block.innerHTML.includes('Complete the Look') ||
            block.dataset.blockType === 'complete_look'
          )) {
            console.log('Removing product block containing Complete the Look:', block);
            block.remove();
          }
        });

        // Remove fitting room elements during preprocessing
        const fittingRoomSelectors = [
          '[class*="fitting-room"]',
          '[data-fitting-room]',
          '[id*="fitting-room"]',
          '[id*="fitting_room"]',
          '[class*="fitting_room"]',
          '[data-fitting_room]',
          '.fitting-room',
          '.fitting_room',
          'div[id*="fitting"]',
          'div[class*="fitting"]'
        ];

        fittingRoomSelectors.forEach(selector => {
          try {
            const elements = productElement.querySelectorAll(selector);
            elements.forEach(element => element.remove());
          } catch (e) {
            // Ignore selector errors
          }
        });
      }

      preventDuplicatedIDs(productElement) {
        const sectionId = productElement.dataset.section;

        const oldId = sectionId;
        const newId = `quickadd-${sectionId}`;
        productElement.innerHTML = productElement.innerHTML.replaceAll(oldId, newId);
        Array.from(productElement.attributes).forEach((attribute) => {
          if (attribute.value.includes(oldId)) {
            productElement.setAttribute(attribute.name, attribute.value.replace(oldId, newId));
          }
        });

        productElement.dataset.originalSection = sectionId;
      }

      removeGalleryListSemantic(productElement) {
        const galleryList = productElement.querySelector('[id^="Slider-Gallery"]');
        if (!galleryList) return;

        galleryList.setAttribute('role', 'presentation');
        galleryList.querySelectorAll('[id^="Slide-"]').forEach((li) => li.setAttribute('role', 'presentation'));
      }

      updateImageSizes(productElement) {
        const product = productElement.querySelector('.product');
        const desktopColumns = product?.classList.contains('product--columns');
        if (!desktopColumns) return;

        const mediaImages = product.querySelectorAll('.product__media img');
        if (!mediaImages.length) return;

        let mediaImageSizes =
          '(min-width: 1000px) 715px, (min-width: 750px) calc((100vw - 11.5rem) / 2), calc(100vw - 4rem)';

        if (product.classList.contains('product--medium')) {
          mediaImageSizes = mediaImageSizes.replace('715px', '605px');
        } else if (product.classList.contains('product--small')) {
          mediaImageSizes = mediaImageSizes.replace('715px', '495px');
        }

        mediaImages.forEach((img) => img.setAttribute('sizes', mediaImageSizes));
      }

      extractShippingTransparencyData(responseHTML, productElement) {
        // Find shipping transparency data script in the full page HTML
        const scripts = responseHTML.querySelectorAll('script');
        let shippingData = null;
        let originalSectionId = null;
        let apiProxyScript = null;

        scripts.forEach(script => {
          const scriptContent = script.textContent || script.innerHTML;
          
          // Extract shipping transparency data
          if (scriptContent.includes('window.shippingTransparencyData')) {
            const sectionIdMatch = scriptContent.match(/window\.shippingTransparencyData\['([^']+)'\]/);
            const dataMatch = scriptContent.match(/window\.shippingTransparencyData\['[^']+'\]\s*=\s*({[\s\S]*?});/);
            
            if (sectionIdMatch && dataMatch) {
              originalSectionId = sectionIdMatch[1];
              try {
                // Clean up the JSON string before parsing
                let jsonString = dataMatch[1];
                // Remove any trailing commas before closing braces/brackets
                jsonString = jsonString.replace(/,(\s*[}\]])/g, '$1');
                shippingData = JSON.parse(jsonString);
              } catch (e) {
                console.warn('Failed to parse shipping transparency data:', e);
                // Continue without shipping data
              }
            }
          }
          
          // Extract API proxy script (contains window.ShippingAPI, window.mockShippingAPI, etc.)
          if (scriptContent.includes('window.ShippingAPI') || 
              scriptContent.includes('window.mockShippingAPI') || 
              scriptContent.includes('window.ShiprocketServerAPI')) {
            apiProxyScript = scriptContent;
          }
        });

        // Store the data for later use with the new section ID
        if (shippingData && originalSectionId) {
          const newSectionId = `quickadd-${originalSectionId}`;
          
          // Initialize the global object if it doesn't exist
          window.shippingTransparencyData = window.shippingTransparencyData || {};
          
          // Store data with the new section ID that will be used after preprocessing
          window.shippingTransparencyData[newSectionId] = shippingData;
          
          // Also store the original section ID for reference
          this.originalSectionId = originalSectionId;
          this.newSectionId = newSectionId;
          
          // Store the API proxy script for later execution
          this.apiProxyScript = apiProxyScript;
        }
      }

      executeApiProxyScript() {
        if (!this.apiProxyScript) {
          console.warn('No API proxy script found to execute');
          return Promise.resolve();
        }

        return new Promise((resolve) => {
          try {
            // Check if API is already available
            if (window.ShippingAPI) {
              console.log('✅ ShippingAPI already available');
              resolve();
              return;
            }

            console.log('🔄 Executing API proxy script for quick view...');
            
            // Create a new script element and execute the API proxy logic
            const scriptElement = document.createElement('script');
            scriptElement.textContent = this.apiProxyScript;
            
            // Add error handling
            scriptElement.onerror = () => {
              console.warn('❌ Failed to execute API proxy script');
              resolve(); // Continue even if script execution fails
            };
            
            // Append to head to execute
            document.head.appendChild(scriptElement);
            
            // Small delay to ensure script execution completes
            setTimeout(() => {
              if (window.ShippingAPI) {
                console.log('✅ API proxy script executed successfully');
              } else {
                console.warn('⚠️ API proxy script executed but ShippingAPI not found');
              }
              resolve();
            }, 100);
            
          } catch (error) {
            console.error('❌ Error executing API proxy script:', error);
            resolve(); // Continue even if there's an error
          }
        });
      }

      loadShippingTransparencyCSS() {
        // Check if CSS is already loaded
        if (document.querySelector('link[href*="section-shipping-transparency.css"]')) {
          return Promise.resolve();
        }
        
        return new Promise((resolve) => {
          // Create and append CSS link
          const cssLink = document.createElement('link');
          cssLink.rel = 'stylesheet';
          cssLink.href = window.Shopify?.routes?.root ? 
            `${window.Shopify.routes.root}assets/section-shipping-transparency.css` :
            '/assets/section-shipping-transparency.css';
          
          cssLink.onload = () => resolve();
          cssLink.onerror = () => resolve(); // Continue even if CSS fails to load
          
          document.head.appendChild(cssLink);
        });
      }

      loadShippingTransparencyJS() {
        // Check if ShippingTransparency class is already available
        if (window.ShippingTransparency) {
          return Promise.resolve();
        }

        // Check if script is already loading/loaded
        if (document.querySelector('script[src*="shipping-transparency.js"]')) {
          return new Promise((resolve) => {
            // Wait for the class to be available
            const checkInterval = setInterval(() => {
              if (window.ShippingTransparency) {
                clearInterval(checkInterval);
                resolve();
              }
            }, 50);
            
            // Timeout after 5 seconds
            setTimeout(() => {
              clearInterval(checkInterval);
              resolve();
            }, 5000);
          });
        }

        return new Promise((resolve) => {
          // Create and append script tag
          const script = document.createElement('script');
          script.src = window.Shopify?.routes?.root ? 
            `${window.Shopify.routes.root}assets/shipping-transparency.js` :
            '/assets/shipping-transparency.js';
          
          script.onload = () => {
            console.log('✅ Shipping transparency JS loaded successfully');
            resolve();
          };
          
          script.onerror = () => {
            console.warn('❌ Failed to load shipping transparency JS');
            resolve(); // Continue even if JS fails to load
          };
          
          document.head.appendChild(script);
        });
      }

      immediatelyHideExtraImages() {
        // Immediately hide all images except the first one to prevent flash of all images
        const mediaGallery = this.modalContent.querySelector('media-gallery');
        if (!mediaGallery) {
          console.log('Quick View: ❌ media-gallery not found in modal content');
          return;
        }

        // Try to find the slider - check multiple possible selectors
        let sliderGallery = mediaGallery.querySelector('[id^="Slider-Gallery"]');
        if (!sliderGallery) {
          console.log('Quick View: ⚠️ Slider-Gallery not found, trying fallback selectors...');
          // Try alternate selectors
          sliderGallery = mediaGallery.querySelector('.product__media-list');
          if (sliderGallery) {
            console.log('Quick View: Found .product__media-list:', sliderGallery);
          }
        }

        // Get all media items - try multiple selectors
        let mediaItems = sliderGallery ? sliderGallery.querySelectorAll('.product__media-item') : [];
        if (mediaItems.length === 0) {
          // Try finding from media-gallery directly
          mediaItems = mediaGallery.querySelectorAll('.product__media-item');
          console.log('Quick View: Searching directly in media-gallery, found:', mediaItems.length);
        }

        if (mediaItems.length === 0) {
          console.log('Quick View: ❌ No .product__media-item elements found');
          console.log('Quick View: Children of media-gallery:', mediaGallery.children.length);
          return;
        }

        console.log('Quick View: Found', mediaItems.length, 'media items, processing...');

        // Ensure FIRST image is always visible and loading eagerly
        const firstItem = mediaItems[0];
        const firstImg = firstItem.querySelector('img');
        if (firstImg) {
          // Force eager loading for immediate display - keep the attribute
          firstImg.setAttribute('loading', 'eager');
          console.log('Quick View: First image set to eager load');
        }

        // Hide all other items, ensure first is visible
        mediaItems.forEach((item, index) => {
          if (index === 0) {
            // First item - ensure visible with inline styles
            item.style.display = '';
            item.style.visibility = '';
            item.style.opacity = '';
          } else {
            // Hide extra items
            item.style.display = 'none';
          }
        });

        // Hide thumbnail slider
        const thumbnailSlider = mediaGallery.querySelector('[id^="Slider-Thumbnails"]');
        if (thumbnailSlider) {
          thumbnailSlider.style.display = 'none';
        }

        // Remove navigation controls
        if (sliderGallery) {
          const wrapper = sliderGallery.closest('.product__media-wrapper') || sliderGallery.parentElement;
          if (wrapper) {
            wrapper.querySelectorAll('.slider-button, .slider-counter, .slider-dots').forEach(el => el.remove());
          }
        }

        console.log('Quick View: ✅ Processed', mediaItems.length, 'images');
      }

      /**
       * Fallback method to ensure first image is visible
       * Called after a short delay to handle any race conditions
       */
      ensureFirstImageVisible() {
        const firstItem = this.modalContent.querySelector('.product__media-item');
        if (!firstItem) {
          console.log('Quick View: ⚠️ ensureFirstImageVisible found no .product__media-item');
          return;
        }

        const img = firstItem.querySelector('img');
        if (img) {
          // Check if image has valid src
          const src = img.src || img.dataset.src || img.srcset;
          console.log('Quick View: First image details - src:', src ? 'present' : 'MISSING', 'loading attr:', img.getAttribute('loading'));

          // If image uses data-src, copy to src
          if (img.dataset.src && !img.src) {
            img.src = img.dataset.src;
            console.log('Quick View: Copied data-src to src');
          }
        }

        // Ensure first item is visible
        firstItem.style.display = 'block';
        firstItem.style.visibility = 'visible';
        firstItem.style.opacity = '1';

        // Ensure image inside is visible
        if (img) {
          img.style.display = 'block';
          img.style.visibility = 'visible';
          img.style.opacity = '1';
        }

        console.log('Quick View: ensureFirstImageVisible completed');
      }

      addTouchSupportToGallery(sliderGallery) {
        let touchStartX = 0;
        let touchEndX = 0;
        let touchStartY = 0;
        let isDragging = false;

        // Add touch event listeners
        const handleTouchStart = (e) => {
          touchStartX = e.touches[0].clientX;
          touchStartY = e.touches[0].clientY;
          isDragging = true;
        };

        const handleTouchMove = (e) => {
          if (!isDragging) return;
          
          touchEndX = e.touches[0].clientX;
          const touchEndY = e.touches[0].clientY;
          
          const diffX = Math.abs(touchStartX - touchEndX);
          const diffY = Math.abs(touchStartY - touchEndY);
          
          // If horizontal swipe is dominant, prevent vertical scroll
          if (diffX > diffY && diffX > 10) {
            e.preventDefault();
          }
        };

        const handleTouchEnd = (e) => {
          if (!isDragging) return;
          isDragging = false;
          
          const diffX = touchStartX - touchEndX;
          
          // Only trigger swipe if horizontal movement is significant
          if (Math.abs(diffX) > 50) {
            const currentIndex = Math.round(sliderGallery.scrollLeft / sliderGallery.offsetWidth);
            const mediaItems = sliderGallery.querySelectorAll('.product__media-item');
            
            if (diffX > 0 && currentIndex < mediaItems.length - 1) {
              // Swipe left - next image
              mediaItems[currentIndex + 1].scrollIntoView({ behavior: 'smooth', block: 'nearest', inline: 'start' });
            } else if (diffX < 0 && currentIndex > 0) {
              // Swipe right - previous image
              mediaItems[currentIndex - 1].scrollIntoView({ behavior: 'smooth', block: 'nearest', inline: 'start' });
            }
          }
        };

        sliderGallery.addEventListener('touchstart', handleTouchStart, { passive: true });
        sliderGallery.addEventListener('touchmove', handleTouchMove, { passive: false });
        sliderGallery.addEventListener('touchend', handleTouchEnd, { passive: true });

        // Prevent default drag on images
        const images = sliderGallery.querySelectorAll('img');
        images.forEach(img => {
          img.addEventListener('dragstart', (e) => e.preventDefault());
        });
      }

      createNavButton(direction, sliderGallery) {
        const button = document.createElement('button');
        button.className = `slider-button slider-button--${direction}`;
        button.type = 'button';
        button.innerHTML = direction === 'prev' 
          ? '<svg viewBox="0 0 24 24" fill="none"><path d="M15 18L9 12L15 6" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round"/></svg>'
          : '<svg viewBox="0 0 24 24" fill="none"><path d="M9 18L15 12L9 6" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round"/></svg>';
        
        button.addEventListener('click', () => {
          const currentIndex = Math.round(sliderGallery.scrollLeft / sliderGallery.offsetWidth);
          const newIndex = direction === 'prev' ? currentIndex - 1 : currentIndex + 1;
          const mediaItems = sliderGallery.querySelectorAll('.product__media-item');
          
          if (newIndex >= 0 && newIndex < mediaItems.length) {
            mediaItems[newIndex].scrollIntoView({ behavior: 'smooth', block: 'nearest', inline: 'start' });
          }
        });
        
        return button;
      }

      addCounterAndDots(wrapper, sliderGallery, totalImages) {
        // Add counter
        const counter = document.createElement('div');
        counter.className = 'slider-counter';
        counter.textContent = `1 / ${totalImages}`;
        wrapper.appendChild(counter);

        // Add dot indicators
        const dotsContainer = document.createElement('div');
        dotsContainer.className = 'slider-dots';
        
        for (let i = 0; i < totalImages; i++) {
          const dot = document.createElement('div');
          dot.className = `slider-dot${i === 0 ? ' active' : ''}`;
          dotsContainer.appendChild(dot);
        }
        
        wrapper.appendChild(dotsContainer);

        // Update counter and dots on scroll
        let scrollTimeout;
        sliderGallery.addEventListener('scroll', () => {
          clearTimeout(scrollTimeout);
          scrollTimeout = setTimeout(() => {
            const currentIndex = Math.round(sliderGallery.scrollLeft / sliderGallery.offsetWidth);
            counter.textContent = `${currentIndex + 1} / ${totalImages}`;
            
            const dots = dotsContainer.querySelectorAll('.slider-dot');
            dots.forEach((dot, index) => {
              dot.classList.toggle('active', index === currentIndex);
            });
          }, 50);
        });
      }

      initializeShippingTransparency() {
        // Add a small delay to ensure DOM is fully rendered
        setTimeout(() => {
          // Find shipping transparency elements in the modal and initialize them
          const shippingElements = this.modalContent.querySelectorAll('.shipping-transparency');
          
          if (shippingElements.length === 0) {
            console.log('No shipping transparency elements found in quick view');
            return;
          }

          // Check if dependencies are available
          if (!window.ShippingTransparency) {
            console.warn('⚠️ ShippingTransparency class not available, skipping initialization');
            return;
          }

          if (!window.ShippingAPI && !window.mockShippingAPI) {
            console.warn('⚠️ Shipping API not available, functionality may be limited');
          }
          
          shippingElements.forEach(element => {
            // Make sure the element doesn't already have an instance
            if (!element.shippingTransparencyInstance) {
              console.log('🚚 Initializing shipping transparency for quick view:', element);
              
              try {
                element.shippingTransparencyInstance = new ShippingTransparency(element);
                
                // Force check button state after initialization with multiple attempts
                const input = element.querySelector('.shipping-transparency__input');
                const button = element.querySelector('.shipping-transparency__button');
                
                if (input && button) {
                  // Multiple attempts to ensure button state is correct
                  const updateButtonState = () => {
                    const value = input.value.trim();
                    button.disabled = value.length !== 6;
                    console.log('Button state updated:', {
                      inputValue: value,
                      buttonDisabled: button.disabled
                    });
                  };
                  
                  // Immediate update
                  updateButtonState();
                  
                  // Trigger input event to update button state
                  const event = new Event('input', { bubbles: true });
                  input.dispatchEvent(event);
                  
                  // Additional delayed updates to ensure state is correct
                  setTimeout(updateButtonState, 100);
                  setTimeout(updateButtonState, 300);
                  
                  console.log('✅ Quick view shipping transparency initialized:', {
                    inputValue: input.value,
                    buttonDisabled: button.disabled,
                    hasInstance: !!element.shippingTransparencyInstance,
                    sectionId: element.dataset.sectionId
                  });
                }
              } catch (error) {
                console.error('❌ Failed to initialize shipping transparency:', error);
              }
            } else {
              console.log('Shipping transparency already initialized for element');
            }
          });
        }, 200); // Slightly longer delay to ensure all dependencies are ready
      }
    }
  );
}
